import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../../../utils";
import { GetAllLaboratoryDTO, GetAllLaboratoryFiltersDTO, GetAllSampleCollectionDTO, GetAllSampleCollectionFiltersDTO, SampleCollectionCreationDTO, SampleCollectionDTO, SampleCollectionUpdateDTO } from "./dataservice/dto";
import { NewLaboratoryService, NewSampleCollectionService } from "./dataservice/service";

interface SampleCollectionState {
    // REQUEST
    sampleCollectionCreationRequest: SampleCollectionCreationDTO
    sampleCollectionUpdateRequest: SampleCollectionUpdateDTO
    sampleCollectionIdToUpdate: string
    sampleCollectionIdentifier: string

    // STATUS
    sampleCollectionCreationStatus: PromiseStatuses
    sampleCollectionUpdateStatus: PromiseStatuses
    sampleCollectionDeleteStatus: PromiseStatuses
    getAllSampleCollectionStatus: PromiseStatuses
    getSampleCollectionByIdStatus: PromiseStatuses

    // RESPONSE
    getAllSampleCollectionResponse?: GetAllSampleCollectionDTO
    getSampleCollectionByIdResponse?: SampleCollectionDTO

    /* LABORATORY */

    // STATUS
    getAllLaboratoryStatus: PromiseStatuses

    // RESPONSE
    getAllLaboratoryResponse?: GetAllLaboratoryDTO
}

const initialState: SampleCollectionState = {
    sampleCollectionCreationRequest: {},
    sampleCollectionUpdateRequest: {},
    sampleCollectionIdToUpdate: '',
    sampleCollectionIdentifier: '',

    sampleCollectionCreationStatus: 'idle',
    sampleCollectionDeleteStatus: 'idle',
    sampleCollectionUpdateStatus: 'idle',
    getAllSampleCollectionStatus: 'idle',
    getSampleCollectionByIdStatus: 'idle',

    getAllLaboratoryStatus: 'idle'
}

export const SampleCollectionCreation = createAsyncThunk(
    'SampleCollection/Creation',
    async (data: SampleCollectionCreationDTO, thunkApi): Promise<void> => {
        const SampleCollectionService = NewSampleCollectionService()
        return SampleCollectionService.CreateSampleCollection(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const SampleCollectionUpdate = createAsyncThunk(
    'SampleCollection/Update',
    async (request: { body: SampleCollectionUpdateDTO, id: string }, thunkApi): Promise<void> => {
        const SampleCollectionService = NewSampleCollectionService()
        return SampleCollectionService.UpdateSampleCollection(request.body, request.id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const SampleCollectionDelete = createAsyncThunk(
    'SampleCollection/Delete',
    async (id: string, thunkApi): Promise<void> => {
        const SampleCollectionService = NewSampleCollectionService()
        return SampleCollectionService.DeleteSampleCollection(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAllSampleCollection = createAsyncThunk(
    'SampleCollection/GetAll',
    async (filters: GetAllSampleCollectionFiltersDTO, thunkApi): Promise<GetAllSampleCollectionDTO> => {
        const SampleCollectionService = NewSampleCollectionService()
        return SampleCollectionService.GetAllSampleCollection(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetSampleCollectionById = createAsyncThunk(
    'SampleCollection/GetById',
    async (id: string, thunkApi): Promise<SampleCollectionDTO> => {
        const SampleCollectionService = NewSampleCollectionService()
        return SampleCollectionService.GetSampleCollectionById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAllLaboratory = createAsyncThunk(
    'laboratory/GetAll',
    async (filters: GetAllLaboratoryFiltersDTO, thunkApi): Promise<GetAllLaboratoryDTO> => {
        const LaboratoryService = NewLaboratoryService()
        return LaboratoryService.GetAllLaboratory(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

const SampleCollectionSlice = createSlice({
    name: 'sampleCollection/slice',
    initialState,
    reducers: {
        // REQUEST [CREATION]
        setSCCreationInspectionVisitId: (state, action) => {
            state.sampleCollectionCreationRequest.inspectionVisitId = action.payload
        },
        setSCCreationSamplingTime: (state, action) => {
            state.sampleCollectionCreationRequest.samplingTime = action.payload
        },
        setSCCreationSamplingDate: (state, action) => {
            state.sampleCollectionCreationRequest.samplingDate = action.payload
        },
        setSCCreationIdentifierCode: (state, action) => {
            state.sampleCollectionCreationRequest.identifierCode = action.payload
        },
        setSCCreationEnvelopeToLaboratory: (state, action) => {
            state.sampleCollectionCreationRequest.envelopeToLaboratory = action.payload
        },
        setSCCreationEnvelopeToCustomer: (state, action) => {
            state.sampleCollectionCreationRequest.envelopeToCustomer = action.payload
        },
        setSCCreationFirstEnvelopeToCodex: (state, action) => {
            state.sampleCollectionCreationRequest.firstEnvelopeToCodex = action.payload
        },
        setSCCreationSecondEnvelopeToCodex: (state, action) => {
            state.sampleCollectionCreationRequest.secondEnvelopeToCodex = action.payload
        },
        setSCCreationMaterialSampled: (state, action) => {
            state.sampleCollectionCreationRequest.materialSampled = action.payload
        },
        setSCCreationLotNumber: (state, action) => {
            state.sampleCollectionCreationRequest.lotNumber = action.payload
        },
        setSCCreationCollectionUnit: (state, action) => {
            state.sampleCollectionCreationRequest.collectionUnit = action.payload
        },
        setSCCreationLaboratoryId: (state, action) => {
            state.sampleCollectionCreationRequest.laboratoryId = action.payload
        },
        setSCCreationNote: (state, action) => {
            state.sampleCollectionCreationRequest.note = action.payload
        },
        setSCCreationPlannedSampling: (state, action) => {
            state.sampleCollectionCreationRequest.plannedSampling = action.payload
        },
        setSCCreationSamplingCompleted: (state, action) => {
            state.sampleCollectionCreationRequest.samplingCompleted = action.payload
        },
        setSCCreationSamplingResult: (state, action) => {
            state.sampleCollectionCreationRequest.samplingResult = action.payload
        },
        setSCCreationPrinciplesFound: (state, action) => {
            state.sampleCollectionCreationRequest.principlesFound = action.payload
        },
        setSCCreationMultiresiduale: (state, action) => {
            state.sampleCollectionCreationRequest.multiresiduale = action.payload
        },
        setSCCreationFosetylAl: (state, action) => {
            state.sampleCollectionCreationRequest.fosetylAl = action.payload
        },
        setSCCreationGlifosate: (state, action) => {
            state.sampleCollectionCreationRequest.glifosate = action.payload
        },
        setSCCreationOgm: (state, action) => {
            state.sampleCollectionCreationRequest.ogm = action.payload
        },
        setSCCreationCloratiPerclorati: (state, action) => {
            state.sampleCollectionCreationRequest.cloratiPerclorati = action.payload
        },
        setSCCreationClormequatMepiquat: (state, action) => {
            state.sampleCollectionCreationRequest.clormequatMepiquat = action.payload
        },
        setSCCreationOther: (state, action) => {
            state.sampleCollectionCreationRequest.other = action.payload
        },
        setSCCreationOtherDetail: (state, action) => {
            state.sampleCollectionCreationRequest.otherDetail = action.payload
        },
        setSCCreationTestReportNumber: (state, action) => {
            state.sampleCollectionCreationRequest.testReportNumber = action.payload
        },
        setSCCreationTestReportDate: (state, action) => {
            state.sampleCollectionCreationRequest.testReportDate = action.payload
        },
        setSCCreationEntryProtocolId: (state, action) => {
            state.sampleCollectionCreationRequest.entryProtocolId = action.payload
        },
        setSCCreationTestReportResult: (state, action) => {
            state.sampleCollectionCreationRequest.testReportResult = action.payload
        },
        setSCCreationEvaluationDate: (state, action) => {
            state.sampleCollectionCreationRequest.evaluationDate = action.payload
        },
        setSCCreationOfficerId: (state, action) => {
            state.sampleCollectionCreationRequest.officerId = action.payload
        },
        resetSampleCollectionCreationRequest: (state) => {
            state.sampleCollectionCreationRequest = {}
        },

        // REQUEST [UPDATE]
        setSampleCollectionIdToUpdate: (state, action) => {
            state.sampleCollectionIdToUpdate = action.payload
        },
        setSampleCollectionIdentifier: (state, action) => {
            state.sampleCollectionIdentifier = action.payload
        },
        setSampleCollectionUpdateRequest: (state, action) => {
            state.sampleCollectionUpdateRequest = action.payload
        },
        setSCUpdateSamplingTime: (state, action) => {
            state.sampleCollectionUpdateRequest.samplingTime = action.payload
        },
        setSCUpdateSamplingDate: (state, action) => {
            state.sampleCollectionUpdateRequest.samplingDate = action.payload
        },
        setSCUpdateMaterialSampled: (state, action) => {
            state.sampleCollectionUpdateRequest.materialSampled = action.payload
        },
        setSCUpdateLotNumber: (state, action) => {
            state.sampleCollectionUpdateRequest.lotNumber = action.payload
        },
        setSCUpdateCollectionUnit: (state, action) => {
            state.sampleCollectionUpdateRequest.collectionUnit = action.payload
        },
        setSCUpdateLaboratoryId: (state, action) => {
            state.sampleCollectionUpdateRequest.laboratoryId = action.payload
        },
        setSCUpdateNote: (state, action) => {
            state.sampleCollectionUpdateRequest.note = action.payload
        },
        setSCUpdatePlannedSampling: (state, action) => {
            state.sampleCollectionUpdateRequest.plannedSampling = action.payload
        },
        setSCUpdateSamplingCompleted: (state, action) => {
            state.sampleCollectionUpdateRequest.samplingCompleted = action.payload
        },
        setSCUpdateSamplingResult: (state, action) => {
            state.sampleCollectionUpdateRequest.samplingResult = action.payload
        },
        setSCUpdatePrinciplesFound: (state, action) => {
            state.sampleCollectionUpdateRequest.principlesFound = action.payload
        },
        setSCUpdateMultiresiduale: (state, action) => {
            state.sampleCollectionUpdateRequest.multiresiduale = action.payload
        },
        setSCUpdateFosetylAl: (state, action) => {
            state.sampleCollectionUpdateRequest.fosetylAl = action.payload
        },
        setSCUpdateGlifosate: (state, action) => {
            state.sampleCollectionUpdateRequest.glifosate = action.payload
        },
        setSCUpdateOgm: (state, action) => {
            state.sampleCollectionUpdateRequest.ogm = action.payload
        },
        setSCUpdateCloratiPerclorati: (state, action) => {
            state.sampleCollectionUpdateRequest.cloratiPerclorati = action.payload
        },
        setSCUpdateClormequatMepiquat: (state, action) => {
            state.sampleCollectionUpdateRequest.clormequatMepiquat = action.payload
        },
        setSCUpdateOther: (state, action) => {
            state.sampleCollectionUpdateRequest.other = action.payload
        },
        setSCUpdateOtherDetail: (state, action) => {
            state.sampleCollectionUpdateRequest.otherDetail = action.payload
        },
        setSCUpdateTestReportNumber: (state, action) => {
            state.sampleCollectionUpdateRequest.testReportNumber = action.payload
        },
        setSCUpdateTestReportDate: (state, action) => {
            state.sampleCollectionUpdateRequest.testReportDate = action.payload
        },
        setSCUpdateEntryProtocolId: (state, action) => {
            state.sampleCollectionUpdateRequest.entryProtocolId = action.payload
        },
        setSCUpdateTestReportResult: (state, action) => {
            state.sampleCollectionUpdateRequest.testReportResult = action.payload
        },
        setSCUpdateEvaluationDate: (state, action) => {
            state.sampleCollectionUpdateRequest.evaluationDate = action.payload
        },
        setSCUpdateOfficerId: (state, action) => {
            state.sampleCollectionUpdateRequest.officerId = action.payload
        },
        resetSampleCollectionUpdateRequest: (state) => {
            state.sampleCollectionUpdateRequest = {}
        },

        // STATUS
        resetSampleCollectionCreationStatus: (state) => {
            state.sampleCollectionCreationStatus = 'idle'
        },
        resetSampleCollectionUpdateStatus: (state) => {
            state.sampleCollectionUpdateStatus = 'idle'
        },
        resetSampleCollectionDeleteStatus: (state) => {
            state.sampleCollectionDeleteStatus = 'idle'
        },
        resetGetAllSampleCollectionStatus: (state) => {
            state.getAllSampleCollectionStatus = 'idle'
        },
        resetGetSampleCollectionByIdStatus: (state) => {
            state.getSampleCollectionByIdStatus = 'idle'
        },
        
        /* LABORATORY */

        // STATUS
        resetGetAllLaboratoryStatus: (state) => {
            state.getAllLaboratoryStatus = 'idle'
        }
    },
    extraReducers(builder) {
        builder

            // CREATION
            .addCase(SampleCollectionCreation.pending, (state) => {
                state.sampleCollectionCreationStatus = 'loading'
            })
            .addCase(SampleCollectionCreation.fulfilled, (state) => {
                state.sampleCollectionCreationStatus = 'successfully'
            })
            .addCase(SampleCollectionCreation.rejected, (state, action) => {
                state.sampleCollectionCreationStatus = 'failed'
            })

            // UPDATE
            .addCase(SampleCollectionUpdate.pending, (state) => {
                state.sampleCollectionUpdateStatus = 'loading'
            })
            .addCase(SampleCollectionUpdate.fulfilled, (state) => {
                state.sampleCollectionUpdateStatus = 'successfully'
            })
            .addCase(SampleCollectionUpdate.rejected, (state, action) => {
                state.sampleCollectionUpdateStatus = 'failed'
            })

            // CREATION
            .addCase(SampleCollectionDelete.pending, (state) => {
                state.sampleCollectionDeleteStatus = 'loading'
            })
            .addCase(SampleCollectionDelete.fulfilled, (state) => {
                state.sampleCollectionDeleteStatus = 'successfully'
            })
            .addCase(SampleCollectionDelete.rejected, (state, action) => {
                state.sampleCollectionDeleteStatus = 'failed'
            })

            // CREATION
            .addCase(GetAllSampleCollection.pending, (state) => {
                state.getAllSampleCollectionStatus = 'loading'
            })
            .addCase(GetAllSampleCollection.fulfilled, (state, action) => {
                state.getAllSampleCollectionStatus = 'successfully'
                state.getAllSampleCollectionResponse = action.payload
            })
            .addCase(GetAllSampleCollection.rejected, (state, action) => {
                state.getAllSampleCollectionStatus = 'failed'
            })

            // CREATION
            .addCase(GetSampleCollectionById.pending, (state) => {
                state.getSampleCollectionByIdStatus = 'loading'
            })
            .addCase(GetSampleCollectionById.fulfilled, (state, action) => {
                state.getSampleCollectionByIdStatus = 'successfully'
                state.getSampleCollectionByIdResponse = action.payload
            })
            .addCase(GetSampleCollectionById.rejected, (state, action) => {
                state.getSampleCollectionByIdStatus = 'failed'
            })

            /* LABORATORY */

            // GET ALL
            .addCase(GetAllLaboratory.pending, (state) => {
                state.getAllLaboratoryStatus = 'loading'
            })
            .addCase(GetAllLaboratory.fulfilled, (state, action) => {
                state.getAllLaboratoryStatus = 'successfully'
                state.getAllLaboratoryResponse = action.payload
            })
            .addCase(GetAllLaboratory.rejected, (state, action) => {
                state.getAllLaboratoryStatus = 'failed'
            })
    },
})

export const {
    // REQUEST
    setSCCreationCloratiPerclorati,
    setSCCreationClormequatMepiquat,
    setSCCreationCollectionUnit,
    setSCCreationEntryProtocolId,
    setSCCreationEnvelopeToCustomer,
    setSCCreationEnvelopeToLaboratory,
    setSCCreationEvaluationDate,
    setSCCreationFirstEnvelopeToCodex,
    setSCCreationFosetylAl,
    setSCCreationGlifosate,
    setSCCreationIdentifierCode,
    setSCCreationInspectionVisitId,
    setSCCreationLaboratoryId,
    setSCCreationLotNumber,
    setSCCreationMaterialSampled,
    setSCCreationMultiresiduale,
    setSCCreationNote,
    setSCCreationOfficerId,
    setSCCreationOgm,
    setSCCreationOther,
    setSCCreationOtherDetail,
    setSCCreationPlannedSampling,
    setSCCreationPrinciplesFound,
    setSCCreationSamplingCompleted,
    setSCCreationSamplingDate,
    setSCCreationSamplingResult,
    setSCCreationSamplingTime,
    setSCCreationSecondEnvelopeToCodex,
    setSCCreationTestReportDate,
    setSCCreationTestReportNumber,
    setSCCreationTestReportResult,
    setSCUpdateCloratiPerclorati,
    setSCUpdateClormequatMepiquat,
    setSCUpdateCollectionUnit,
    setSCUpdateEntryProtocolId,
    setSCUpdateEvaluationDate,
    setSCUpdateFosetylAl,
    setSCUpdateGlifosate,
    setSCUpdateLaboratoryId,
    setSampleCollectionIdentifier,
    setSCUpdateLotNumber,
    setSCUpdateMaterialSampled,
    setSCUpdateMultiresiduale,
    setSCUpdateNote,
    setSCUpdateOfficerId,
    setSCUpdateOgm,
    setSCUpdateOther,
    setSCUpdateOtherDetail,
    setSCUpdatePlannedSampling,
    setSCUpdatePrinciplesFound,
    setSCUpdateSamplingCompleted,
    setSCUpdateSamplingDate,
    setSCUpdateSamplingResult,
    setSCUpdateSamplingTime,
    setSCUpdateTestReportDate,
    setSCUpdateTestReportNumber,
    setSCUpdateTestReportResult,
    setSampleCollectionIdToUpdate,
    setSampleCollectionUpdateRequest,
    resetSampleCollectionCreationRequest,
    resetSampleCollectionUpdateRequest,

    // STATUS
    resetGetAllSampleCollectionStatus,
    resetGetSampleCollectionByIdStatus,
    resetSampleCollectionCreationStatus,
    resetSampleCollectionDeleteStatus,
    resetSampleCollectionUpdateStatus,

    /* LABORATORY */

    // STATUS
    resetGetAllLaboratoryStatus
} = SampleCollectionSlice.actions

export default SampleCollectionSlice.reducer